import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { Container } from "components";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "components/molecules/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getData, postData, putData } from "actions";
import { API_URL_group, API_URL_permission } from "constants/api";
import { groupReducers } from "redux/groupSlice";
import { permissionReducers } from "redux/permissionSlice";
import { AuthContext } from "context/AuthContext";

const FormGroups = ({ typeForm }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const dispatch = useDispatch();
  const inputRefs = useRef([]);
  const { updateUser, datauser, setUpdateUser } = useContext(AuthContext);

  const { getPermissionResult } = useSelector((state) => state.permission);

  const formik = useFormik({
    initialValues: {
      name: location.state ? location.state.item.name : "",
      status: location.state ? location.state.item.status : "",
      permissions: location.state ? location.state.item.permissions : [],
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      status: Yup.string().required("Status is required"),
    }),
    onSubmit: (values) => {
      const formData = values;

      if (location.state === null) {
        postData(
          API_URL_group,
          formData,
          { dispatch, redux: groupReducers },
          "ADD_GROUP",
          () => {
            navigate("/administrator/staff/groups");
          }
        );
      } else {
        putData(
          `${API_URL_group}${location.state.item.id}/`,
          formData,
          { dispatch, redux: groupReducers },
          "ADD_GROUP",
          () => {
            setUpdateUser(!updateUser);
            navigate("/administrator/staff/groups");
          }
        );
      }
    },
  });

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_permission,
        params,
        { dispatch, redux: permissionReducers },
        "GET_PERMISSION"
      );
    },
    [dispatch]
  );

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  useEffect(() => {
    const params = `?content_type=${1000}`;
    get(params);
  }, [get]);

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Staff"}
        excludedPaths={["staff", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          Create a group
        </div>
        <form onSubmit={handleFormSubmit}>
          <div className="h-auto w-auto px-4 bg-neutral dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-600 object-left">
            <div className="mt-4 grid grid-cols-12 gap-5">
              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-1 lg:col-span-6 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Nama <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[0] = ref)}
                    value={formik.values.name}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.name}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-7 lg:col-span-6 lg:col-start-7">
                <label className="text-xs md:-text-sm lg:text-sm">Status</label>
                <div className="flex flex-wrap">
                  <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                    <input
                      id="show"
                      type="radio"
                      checked={formik.values.status === 1}
                      onChange={() => formik.setFieldValue("status", 1)}
                      ref={(ref) => (inputRefs.current[1] = ref)}
                      className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                    />
                    <span className="ml-2 mr-5">Show</span>
                  </label>
                  <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                    <input
                      id="hidden"
                      type="radio"
                      checked={formik.values.status === 0}
                      onChange={() => formik.setFieldValue("status", 0)}
                      ref={(ref) => (inputRefs.current[2] = ref)}
                      className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                    />
                    <span className="ml-2">Hidden</span>
                  </label>
                </div>
                {formik.touched.status && formik.errors.status && (
                  <div className="text-red-500 dark:text-red-300 text-xs">
                    {formik.errors.status}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-neutral dark:bg-neutral-800 rounded shadow my-6">
              <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
                Permissions
              </div>

              <div className="px-3 py-1 h-fit overflow-y-auto">
                {getPermissionResult &&
                  getPermissionResult.map((item, index) => (
                    <div key={index}>
                      <label className="text-xs md:-text-sm lg:text-sm">
                        <input
                          id={item.codename}
                          type="checkbox"
                          className="form-checkbox mr-2 mt-2"
                          checked={formik.values.permissions.includes(item.id)}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            if (isChecked) {
                              formik.setFieldValue("permissions", [
                                ...formik.values.permissions,
                                item.id,
                              ]);
                            } else {
                              formik.setFieldValue(
                                "permissions",
                                formik.values.permissions.filter(
                                  (id) => id !== item.id
                                )
                              );
                            }
                          }}
                        />
                        {item.name}
                      </label>
                      <br />
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div className="h-12 w-full grid items-center px-4 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            <button
              type="submit"
              className="w-20 items-center p-1 px-3 rounded shadow-sm border text-white bg-blue-800 hover:bg-blue-700"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default FormGroups;
